import { useCallback, useEffect } from "react";
import { mutate, useSWRConfig } from "swr";
import useSWRImmutable from "swr/immutable";
import { useApi } from "../api";
import { CalculationEntry } from "./types/calculationEntries";

const buildKey = (calculationId: string) => `/modules/concrete_resin/calculations/${calculationId}/entries`;

/**
 * The calculation's saved entries. They come with the calculation's load (its data.entries), then
 * they are kept on the client only: the saves' responses carry none, and the changes show at once.
 */
export const useCalculationEntries = (
  calculationId: string | undefined,
  loadedEntries: CalculationEntry[] | null | undefined,
): CalculationEntry[] | undefined => {
  const { cache } = useSWRConfig();
  const key = calculationId ? buildKey(calculationId) : null;
  const { data } = useSWRImmutable<CalculationEntry[]>(key, null, { fallbackData: loadedEntries ?? undefined });

  // Seeded from the load once: afterwards the client copy holds the changes, the failed ones too.
  useEffect(() => {
    if (key && loadedEntries && cache.get(key)?.data === undefined) {
      void mutate(key, loadedEntries, { revalidate: false });
    }
  }, [cache, key, loadedEntries]);

  return data;
};

export const useCalculationEntriesApi = () => {
  const api = useApi();

  // Waits for the server, which gives the entry its id and its results.
  const createEntry = useCallback(
    async (calculationId: string): Promise<CalculationEntry> => {
      const url = buildKey(calculationId);
      const entry = await api.post<CalculationEntry, never>(url);
      void mutate<CalculationEntry[]>(url, (entries = []) => [...entries, entry], { revalidate: false });
      return entry;
    },
    [api],
  );

  // Shown at once, then sent: a failure only rejects, the change stays.
  const deleteEntry = useCallback(
    async (calculationId: string, entryId: string): Promise<void> => {
      void mutate<CalculationEntry[]>(
        buildKey(calculationId),
        (entries = []) => entries.filter((entry) => entry.id !== entryId),
        { revalidate: false },
      );
      await api.del(`${buildKey(calculationId)}/${entryId}`);
    },
    [api],
  );

  return { createEntry, deleteEntry };
};
